import { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Loader2, Map as MapIcon } from "lucide-react";

const iso = (d) => d.toISOString().slice(0, 10);

function loadLeaflet() {
  return new Promise((resolve) => {
    if (window.L) return resolve(window.L);
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css"; link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    const sc = document.createElement("script");
    sc.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    sc.onload = () => resolve(window.L);
    sc.onerror = () => resolve(null);
    document.body.appendChild(sc);
  });
}

export default function VanGeoMap() {
  const [from, setFrom] = useState(iso(new Date(Date.now() - 7 * 864e5)));
  const [to, setTo] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layer = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/van-sales/geo-map", { params: { from_date: from, to } })
      .then((r) => setData(r.data)).catch(() => setData({ drivers: [], regions: [], points: [] })).finally(() => setLoading(false));
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = await loadLeaflet();
      if (cancelled || !L || !mapRef.current) return;
      if (!mapObj.current) {
        mapObj.current = L.map(mapRef.current).setView([39.2, 35.2], 6);
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 }).addTo(mapObj.current);
      }
      if (layer.current) layer.current.remove();
      layer.current = L.layerGroup().addTo(mapObj.current);
      const D = data || {};
      // Bölge sahiplik daireleri (renk = sahibi şoför)
      (D.regions || []).forEach((rg) => {
        L.circle([rg.lat, rg.lng], { radius: 130000, color: rg.owner_color, fillColor: rg.owner_color, fillOpacity: 0.18, weight: 1.5 })
          .bindPopup(`<b>${rg.region}</b><br/>Sahip: ${rg.owner_driver_name || "—"}${(rg.grants || []).length ? "<br/>Onaylı istisna: " + rg.grants.map((g) => g.driver_name).join(", ") : ""}`)
          .addTo(layer.current);
        // müdür onayıyla başka şoföre açılan istisna → küçük daire (grantee rengi)
        (rg.grants || []).forEach((g, i) => {
          L.circle([rg.lat + 0.12 * (i + 1), rg.lng + 0.12 * (i + 1)], { radius: 55000, color: g.color, fillColor: g.color, fillOpacity: 0.35, weight: 2, dashArray: "4" })
            .bindPopup(`<b>${rg.region}</b> · onaylı istisna<br/>${g.driver_name}`).addTo(layer.current);
        });
      });
      // Noktalar
      (D.points || []).forEach((p) => {
        if (p.type === "sale") {
          L.circleMarker([p.lat, p.lng], { radius: 6, color: "#fff", weight: 1.5, fillColor: p.color, fillOpacity: 1 })
            .bindPopup(`<b>Satış</b> · ${p.driver_name || ""}<br/>${p.label}<br/><span style="color:#64748b">${p.time}</span>`).addTo(layer.current);
        } else {
          const icon = L.divIcon({ className: "", html: `<div style="width:14px;height:14px;background:${p.color};transform:rotate(45deg);border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.3)"></div>`, iconSize: [14, 14] });
          L.marker([p.lat, p.lng], { icon }).bindPopup(`<b>Kontak</b> · ${p.driver_name || ""}<br/>${p.label}<br/><span style="color:#64748b">${p.time}</span>`).addTo(layer.current);
        }
      });
    })();
    return () => { cancelled = true; };
  }, [data]);

  return (
    <div className="space-y-3" data-testid="van-geo-map">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="block text-xs text-slate-500">Başlangıç</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-testid="geomap-from" /></div>
        <div><label className="block text-xs text-slate-500">Bitiş</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-testid="geomap-to" /></div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        <span className="ml-auto flex items-center gap-1 text-xs text-slate-400"><MapIcon className="h-3.5 w-3.5" /> ● Satış  ◆ Kontak — renk = şoför; daire = bölge sahipliği</span>
      </div>
      <div className="flex flex-wrap gap-3 rounded-lg bg-slate-50 p-2" data-testid="geomap-legend">
        {(data?.drivers || []).length === 0 && <span className="text-xs text-slate-400">Bu aralıkta konumlu veri yok.</span>}
        {(data?.drivers || []).map((d) => (
          <span key={d.id} className="flex items-center gap-1.5 text-xs text-slate-600">
            <span className="inline-block h-3 w-3 rounded-full" style={{ background: d.color }} /> {d.name}
          </span>
        ))}
      </div>
      <div ref={mapRef} className="h-[560px] w-full overflow-hidden rounded-xl border border-slate-200 shadow-sm" data-testid="geomap-canvas" />
    </div>
  );
}
